<template>
  <div class="quick-add-item">
    <img
      v-if="imageUrl"
      :src="imageUrl"
      :alt="bundle.content.name"
      class="quick-add-item__image"
    />
    <div class="quick-add-item__details">
      <div class="quick-add-item__title">{{ bundle.content.name }}</div>
      <div class="quick-add-item__price">
        <span
          v-if="showCompareAtPrice"
          class="quick-add-item__compare-at"
          >{{ formattedCompareAtPrice }}</span
        >
        <span class="quick-add-item__base-price">{{ formattedBasePrice }}</span>
      </div>
      <button
        type="button"
        class="quick-add-item__cta"
        :disabled="disabled"
        @click="$emit('add')"
      >
        <span class="quick-add-item__cta-icon">
          <FontAwesomeIcon :icon="appIcons.faPlus" />
        </span>
        <span>{{ cartSidebarContent.quickAddLabel }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import useAppIcons from '@/composables/appIcons'
import { formatCurrency } from '@/lib/utils'

import { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'

import { cartSidebarContent } from '@/content/cartSidebar'

export default defineComponent({
  name: 'QuickAddItem',
  components: { FontAwesomeIcon },
  props: {
    bundle: {
      type: Object as PropType<CatalogBundleStorefrontDtoWithContent>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['add'],
  setup() {
    return { ...useAppIcons(), cartSidebarContent }
  },
  computed: {
    // Use the bundle's first (or only) listing-view image as the YMAL card thumbnail — `cartListingImage`
    // is rarely authored, whereas listing images always are. (A dedicated YMAL image may land in Contentful
    // later.) Mirrors the `firstListingImage` narrowing in `CartBundleItem`: a `bundleImages` collection has
    // `images[]`, a single `bundleImage` has `image`.
    imageUrl(): string | null {
      const listingViewImages = this.bundle.content.listingViewImages
      if (!listingViewImages) {
        return null
      }

      if ('images' in listingViewImages && listingViewImages.images.length > 0) {
        return listingViewImages.images[0].image?.file?.url ?? null
      } else if ('image' in listingViewImages) {
        return listingViewImages.image?.file?.url ?? null
      }

      return null
    },
    basePrice(): number {
      return this.bundle.pricing?.basePrice ?? 0
    },
    formattedBasePrice(): string {
      return formatCurrency(this.basePrice)
    },
    formattedCompareAtPrice(): string {
      return this.bundle.compareAtPrice !== null ? formatCurrency(this.bundle.compareAtPrice) : ''
    },
    // Mirrors BundlePricing: show the strikethrough compare-at only when it exceeds the base price.
    showCompareAtPrice(): boolean {
      return (
        this.bundle.compareAtPrice !== null &&
        this.bundle.pricing !== null &&
        this.bundle.compareAtPrice > this.bundle.pricing.basePrice
      )
    }
  }
})
</script>

<style scoped lang="scss">
// Dimensions from Figma `10192:196342`: 384×152 card, 144px image, 240px details (16px padding),
// 16px gap between the title block and the CTA, 18px prices, bold "Quick Add". 6px card radius with
// the card-blur drop shadow; the image's own 8px radius is clipped by the card's `overflow: hidden`.
.quick-add-item {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
  width: 24rem;
  min-height: 9.5rem;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 23px rgba(0, 0, 0, 0.06);

  &__image {
    width: 9rem;
    flex-shrink: 0;
    object-fit: cover;
  }

  &__details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1rem;
    min-width: 0;
    padding: 1rem;
  }

  &__title {
    font-weight: 700;
    line-height: 1.5;
  }

  &__price {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: $cady-type-body-lg-size;
    line-height: normal;
  }

  &__compare-at {
    color: $cady-color-gray-600;
    text-decoration: line-through;
  }

  &__base-price {
    font-weight: $cady-type-heading-weight;
  }

  // Figma `16049:30006`: black at radius 40px, 6px/12px padding, 8px gap, a 24px icon box at 18px, and
  // the label at Roboto Bold 16/24 — a 36px-tall pill.
  //
  // Deliberately **not** a `.btn`. Every value `.btn` would supply is pinned here anyway, and opting
  // into that cascade actively broke the glyph: `cady-storefront.css` carries
  // `.btn svg { height: 14px; font-size: .875rem }`, which targets the SVG directly and so beat the
  // `font-size` set on the icon *wrapper* below — the glyph rendered at 14px rather than the 18px this
  // block claims. The rest of that cascade is no friendlier: Bootstrap's `.btn` custom properties, then
  // `cady-storefront.css` overriding `.btn` padding and adding `letter-spacing: 0.5px`, then
  // `.btn-dark`'s near-black `#212529`. A bare `<button>` matches the two the flyout already uses
  // (`cart-sidebar-header__back`, `cart-sidebar-item__remove`).
  &__cta {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem; // 8px
    padding: 0.375rem 0.75rem; // 6px 12px
    border: none;
    border-radius: 40px;
    background-color: $cady-color-black;
    color: $cady-color-white;
    font-size: $cady-type-body-nm-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-body-nm-line-height;
    white-space: nowrap;
    cursor: pointer;
  }

  // The board draws the glyph in a 24px box at 18px; Font Awesome renders at 1em.
  &__cta-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 1.125rem; // 18px
  }
}
</style>
